export function normalizePhone(input) {
  const source = String(input ?? '').trim();
  if (!/^(?:\+|00)?[\d\s().-]+$/.test(source)) return '';
  let digits = source.replace(/[\s()+.\-]/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  return /^[1-9]\d{7,14}$/.test(digits) ? digits : '';
}

export function buildWhatsAppUrl(phone, message) {
  const digits = normalizePhone(phone);
  if (!digits) return '';
  const text = String(message ?? '').trim();
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}

function localUrl(path, origin) {
  try {
    const url = new URL(path, origin);
    return url.origin === new URL(origin).origin ? url : null;
  } catch { return null; }
}

export function composeWhatsAppMessage({ message = '', mode = 'floating', title = '', optionSummary = '', url = '' }) {
  const parts = [String(message).trim()];
  if (mode === 'product') {
    if (title) parts.push(String(title).trim());
    if (optionSummary) parts.push(String(optionSummary).trim());
  }
  if (url) parts.push(url);
  return parts.filter(Boolean).join('\n');
}

export function productLink({ phone, message, title, optionSummary, productUrl, variantId }, origin) {
  const url = localUrl(productUrl, origin);
  if (!url) return '';
  url.search = '';
  url.hash = '';
  if (/^\d+$/.test(String(variantId || ''))) url.searchParams.set('variant', String(variantId));
  return buildWhatsAppUrl(phone, composeWhatsAppMessage({ message, mode: 'product', title, optionSummary: variantId ? optionSummary : '', url: url.href }));
}

export function syncWhatsAppLink(link, origin = location.origin) {
  const { whatsappMode: mode, whatsappPhone: phone, whatsappMessage: message } = link.dataset;
  let href = '';
  if (mode === 'product') {
    const root = link.closest('[data-product-page]');
    if (root) {
      const state = root.querySelector('[data-product-state]');
      const variantId = state?.dataset.variantId || '';
      const fallbackTitle = state?.dataset.variantTitle === 'Default Title' ? '' : state?.dataset.variantTitle || '';
      const optionSummary = [...root.querySelectorAll('[data-product-selected-option]')].map(node => node.textContent.trim()).filter(Boolean).join(' / ') || (variantId ? fallbackTitle : '');
      href = productLink({ phone, message, title: link.dataset.whatsappTitle, optionSummary, productUrl: root.dataset.productUrl, variantId }, origin);
    }
  } else {
    const url = localUrl(link.dataset.whatsappUrl || '/', origin);
    if (url) href = buildWhatsAppUrl(phone, composeWhatsAppMessage({ message, mode, url: mode === 'cart' ? url.href : '' }));
  }
  if (href) { link.href = href; link.hidden = false; }
  else { link.removeAttribute('href'); link.hidden = true; }
}

export function mountWhatsApp(scope = document) {
  const links = [...(scope.matches?.('[data-whatsapp-cta]') ? [scope] : []), ...scope.querySelectorAll('[data-whatsapp-cta]')];
  for (const link of links) syncWhatsAppLink(link);
}

if (typeof document !== 'undefined') {
  mountWhatsApp();
  document.addEventListener('nawiya:product:variant-changed', event => mountWhatsApp(event.target));
  document.addEventListener('shopify:section:load', event => mountWhatsApp(event.target));
  // Late-rendered links (cart drawer, quick view): one re-scan per frame at most. Apps and pixels add thousands of
  // nodes on product pages, so inspecting every added node cost up to ~370 ms of main-thread time (Lighthouse).
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; mountWhatsApp(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
